/*
  LMA : bloc « Set 3 pièces » compact (lma_set_bundle), élément <lma-set-bundle> rendu par
  snippets/lma-set-bundle-selector.liquid. Chargé en defer par le snippet.

  1. Lit, pour chaque ligne [data-set-line], le JSON des variantes émis en Liquid (id, option1..3, available,
     price, price_text, featured_image) : aucun appel réseau pour résoudre les variantes.
  2. À chaque changement d'un <select> Couleur ou Taille : résout la variante de la ligne, désactive les options
     sans combinaison disponible (option disabled + suffixe « Épuisé »), met à jour la pastille de couleur
     (classe is-colour-<valeur>, nuancier admin dans --lma-set-swatch), la vignette, le prix de la ligne, le
     récapitulatif (aria-live) et le total ; le bouton passe en « Épuisé » tant qu'une pièce n'est pas disponible.
  3. Le legging suit le coloris du short ([data-follow="short"], champ caché) : à chaque changement de coloris du
     short, son coloris est recopié si le legging existe dans ce coloris, sinon Noir, sinon le coloris courant.
  4. Galerie : au changement de coloris de la ligne [data-set-gallery-source] (le short), active le premier média
     du produit set dont l'alt OU le nom de fichier contient ce coloris (JSON [data-set-media] : id, alt, file, src)
     via media-gallery.setActiveMedia, sinon par clic sur la vignette correspondante. Sur mobile la galerie défile.
     Vignette de chaque ligne, dans l'ordre : image de la variante choisie ; image d'une autre variante du même
     coloris (les images de variante sont souvent posées sur une seule taille) ; média du produit set au coloris
     choisi (alt ou nom de fichier, de préférence celui qui nomme aussi la pièce) ; image principale du produit.
  5. Au clic sur le bouton : poste les TROIS variantes en UNE requête POST /cart/add.js, items[] avec les
     propriétés _set_automne (identifiant de groupe unique par clic) et _set_role (brassiere / short / legging),
     comme le bloc « 2b » de snippets/luveon-mvp.liquid, puis rafraîchit le drawer : window.monster_refresh si
     présent, sinon cart-notification / cart-drawer Dawn (renderContents avec les sections renvoyées par
     /cart/add.js), sinon refreshContents du drawer, sinon page panier.
  6. Erreur : message affiché dans le bloc et liens de repli vers les fiches produit ; la vente n'est jamais
     bloquée. Aucune dépendance : publish / PUB_SUB_EVENTS / LuveonCartOperations sont utilisés s'ils existent.
*/
(function () {
  'use strict';

  if (window.customElements.get('lma-set-bundle')) return;

  function formatMoney(cents, format) {
    var amount = (Number(cents) || 0) / 100;
    var pattern = format || '{{amount}}';
    var match = pattern.match(/\{\{\s*(\w+)\s*\}\}/);
    var type = match ? match[1] : 'amount';
    var decimals = type.indexOf('no_decimals') > -1 ? 0 : 2;
    var fixed = amount.toFixed(decimals);
    var parts = fixed.split('.');
    var thousands = ',';
    var decimal = '.';
    if (type.indexOf('with_comma_separator') > -1) {
      thousands = '.';
      decimal = ',';
    } else if (type.indexOf('with_space_separator') > -1) {
      thousands = ' ';
      decimal = ',';
    } else if (type.indexOf('with_apostrophe_separator') > -1) {
      thousands = "'";
      decimal = '.';
    }
    var integer = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    var value = parts.length > 1 ? integer + decimal + parts[1] : integer;
    return pattern.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function readJson(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  /* « Bleu Marine » → « bleu-marine », comme le filtre Liquid handleize (accents retirés). */
  function handleize(value) {
    var text = String(value == null ? '' : value).toLowerCase();
    if (typeof text.normalize === 'function') text = text.normalize('NFD').replace(/[̀-ͯ]/g, '');
    return text.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  function escapeRegExp(text) {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  class LmaSetBundle extends HTMLElement {
    constructor() {
      super();
      this.onChange = this.onChange.bind(this);
      this.onAddClick = this.onAddClick.bind(this);
      this.adding = false;
    }

    connectedCallback() {
      this.groupKey = this.dataset.groupKey || '_set_automne';
      this.roleKey = this.dataset.roleKey || '_set_role';
      this.lines = toArray(this.querySelectorAll('[data-set-line]')).map(this.readLine, this);
      this.media = readJson(this.querySelector('[data-set-media]')) || [];
      this.button = this.querySelector('[data-set-add]');
      this.buttonLabel = this.querySelector('[data-set-add-label]');
      this.errorWrapper = this.querySelector('[data-set-error]');
      this.errorText = this.errorWrapper ? this.errorWrapper.querySelector('.product-form__error-message') : null;
      this.fallback = this.querySelector('[data-set-fallback]');
      this.recap = this.querySelector('[data-set-recap]');
      this.totalEl = this.querySelector('[data-set-total]');
      this.baseTotal = parseInt(this.dataset.total, 10) || 0;

      this.addEventListener('change', this.onChange);
      if (this.button) this.button.addEventListener('click', this.onAddClick);
      this.refreshAll();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      if (this.button) this.button.removeEventListener('click', this.onAddClick);
    }

    /* ------------------------------------------------------------ données */

    readLine(el) {
      var variants = readJson(el.querySelector('[data-set-variants]')) || [];
      var options = toArray(el.querySelectorAll('[data-set-option]')).map(function (wrapper) {
        var control = wrapper.querySelector('[data-set-input]');
        var isSelect = !!(control && control.tagName === 'SELECT');
        var colours = null;
        if (isSelect) {
          colours = toArray(control.options).map(function (opt) {
            return {
              value: opt.value,
              handle: opt.getAttribute('data-colour') || handleize(opt.value),
              swatch: opt.getAttribute('data-swatch') || null
            };
          });
        } else {
          colours = readJson(wrapper.querySelector('[data-set-colours]')) || [];
        }
        return {
          el: wrapper,
          kind: wrapper.getAttribute('data-set-option') || 'other',
          index: parseInt(wrapper.getAttribute('data-option-index'), 10),
          control: control,
          isSelect: isSelect,
          follow: wrapper.getAttribute('data-follow') || '',
          swatch: wrapper.hasAttribute('data-set-swatch'),
          echo: wrapper.querySelector('[data-set-echo]'),
          colours: colours
        };
      });
      return {
        el: el,
        role: el.getAttribute('data-role') || '',
        label: el.getAttribute('data-label') || '',
        missing: el.hasAttribute('data-missing') || variants.length === 0,
        gallerySource: el.hasAttribute('data-set-gallery-source'),
        variants: variants,
        options: options,
        thumb: el.querySelector('[data-set-thumb]'),
        hint: el.querySelector('[data-set-line-hint]'),
        priceEl: el.querySelector('[data-set-line-price]'),
        compareEl: el.querySelector('[data-set-line-compare]'),
        free: el.hasAttribute('data-set-free'),
        basePrice: parseInt(el.getAttribute('data-price'), 10) || 0,
        variant: null
      };
    }

    lineByRole(role) {
      for (var i = 0; i < this.lines.length; i++) {
        if (this.lines[i].role === role) return this.lines[i];
      }
      return null;
    }

    lineByElement(el) {
      for (var i = 0; i < this.lines.length; i++) {
        if (this.lines[i].el === el) return this.lines[i];
      }
      return null;
    }

    optionOfKind(line, kind) {
      for (var i = 0; i < line.options.length; i++) {
        if (line.options[i].kind === kind) return line.options[i];
      }
      return null;
    }

    selectionOf(line) {
      var selection = [null, null, null];
      line.options.forEach(function (option) {
        if (!option.control || option.index < 0 || option.index > 2) return;
        var value = option.control.value;
        selection[option.index] = value === '' ? null : value;
      });
      return selection;
    }

    matches(variant, selection) {
      for (var i = 0; i < 3; i++) {
        if (selection[i] == null) continue;
        if (variant['option' + (i + 1)] !== selection[i]) return false;
      }
      return true;
    }

    findVariant(line, selection) {
      for (var i = 0; i < line.variants.length; i++) {
        if (this.matches(line.variants[i], selection)) return line.variants[i];
      }
      return null;
    }

    anyAvailable(line, selection) {
      for (var i = 0; i < line.variants.length; i++) {
        var variant = line.variants[i];
        if (variant.available && this.matches(variant, selection)) return true;
      }
      return false;
    }

    hasValue(line, index, value) {
      for (var i = 0; i < line.variants.length; i++) {
        if (line.variants[i]['option' + (index + 1)] === value) return true;
      }
      return false;
    }

    /* ------------------------------------------------------------ coloris suivi (legging = short) */

    applyFollow(line) {
      var self = this;
      line.options.forEach(function (option) {
        if (!option.follow || !option.control) return;
        var source = self.lineByRole(option.follow);
        if (!source || source.missing) return;
        var sourceOption = self.optionOfKind(source, option.kind);
        if (!sourceOption || !sourceOption.control) return;
        var wanted = sourceOption.control.value;
        var chosen = null;

        if (wanted && self.hasValue(line, option.index, wanted)) {
          chosen = wanted;
        } else {
          var wantedHandle = handleize(wanted);
          var fallbacks = [wantedHandle, 'noir', 'black'];
          for (var f = 0; f < fallbacks.length && !chosen; f++) {
            if (!fallbacks[f]) continue;
            for (var c = 0; c < option.colours.length; c++) {
              if (option.colours[c].handle === fallbacks[f] && self.hasValue(line, option.index, option.colours[c].value)) {
                chosen = option.colours[c].value;
                break;
              }
            }
          }
        }
        if (chosen && option.control.value !== chosen) option.control.value = chosen;
      });
    }

    followersOf(role) {
      return this.lines.filter(function (line) {
        for (var i = 0; i < line.options.length; i++) {
          if (line.options[i].follow === role) return true;
        }
        return false;
      });
    }

    /* ------------------------------------------------------------ rendu */

    paintSwatch(option, value) {
      if (!option.swatch) return;
      var info = null;
      for (var i = 0; i < option.colours.length; i++) {
        if (option.colours[i].value === value) {
          info = option.colours[i];
          break;
        }
      }
      var handle = info ? info.handle : handleize(value);
      var classes = toArray(option.el.classList).filter(function (name) { return name.indexOf('is-colour-') === 0; });
      classes.forEach(function (name) { option.el.classList.remove(name); });
      if (handle) option.el.classList.add('is-colour-' + handle);
      if (info && info.swatch) {
        option.el.style.setProperty('--lma-set-swatch', info.swatch);
      } else {
        option.el.style.removeProperty('--lma-set-swatch');
      }
      if (option.echo) option.echo.textContent = value == null ? '' : value;
    }

    refreshLine(line) {
      if (line.missing) return;
      var self = this;
      this.applyFollow(line);
      var selection = this.selectionOf(line);
      var soldOutText = this.dataset.soldOutText || '';

      line.options.forEach(function (option) {
        if (option.isSelect) {
          toArray(option.control.options).forEach(function (opt) {
            var probe = selection.slice();
            probe[option.index] = opt.value;
            var ok = self.anyAvailable(line, probe);
            opt.disabled = !ok;
            var text = ok || !soldOutText ? opt.value : opt.value + ' — ' + soldOutText;
            if (opt.textContent !== text) opt.textContent = text;
          });
        }
        if (option.kind === 'colour') self.paintSwatch(option, selection[option.index]);
      });

      line.variant = this.findVariant(line, selection);
      var available = !!(line.variant && line.variant.available);
      if (line.hint) line.hint.hidden = available;
      line.el.classList.toggle('is-unavailable', !available);

      if (line.thumb) {
        var colourOption = this.optionOfKind(line, 'colour');
        var colourValue = colourOption && colourOption.control ? colourOption.control.value : '';
        var src = (line.variant && line.variant.featured_image)
          || this.colourVariantImage(line, colourOption, colourValue)
          || this.setMediaImage(colourValue, colourOption ? colourOption.colours : [], line.role)
          || line.thumb.getAttribute('data-fallback') || '';
        this.setThumb(line.thumb, src);
      }
      /* Prix de ligne : recalculés ensemble dans refreshPrices (répartition du prix du set). */
    }

    setThumb(thumb, src) {
      if (!src) return;
      var img = thumb.querySelector('img');
      if (!img) {
        img = document.createElement('img');
        var size = parseInt(getComputedStyle(thumb).width, 10) || 40;
        img.width = size;
        img.height = size;
        img.alt = '';
        img.loading = 'lazy';
        thumb.appendChild(img);
      }
      if (img.getAttribute('src') !== src) img.setAttribute('src', src);
    }

    refreshSummary() {
      var self = this;
      var total = 0;
      var complete = this.dataset.ready === 'true' && this.lines.length > 0;
      var changed = false;
      var recap = [];

      this.lines.forEach(function (line) {
        if (line.missing) {
          complete = false;
          return;
        }
        if (!line.variant || !line.variant.available) complete = false;
        var price = line.variant ? Number(line.variant.price) || 0 : line.basePrice;
        if (price !== line.basePrice) changed = true;
        total += price;
        var values = self.selectionOf(line).filter(function (value) { return value != null && value !== ''; });
        recap.push(line.label + (values.length ? ' : ' + values.join(', ') : ''));
      });

      if (this.totalEl && changed) {
        this.totalEl.textContent = this.formatPrice(total);
      }
      this.refreshPrices();
      if (this.recap) this.recap.textContent = recap.length ? recap.join(' · ') + '. ' : '';

      this.setButtonState(complete);
    }

    /* « 29,90 € » en euros (même rendu que le panier), sinon le format monétaire de la boutique. */
    formatPrice(cents) {
      if ((this.dataset.currency || '').toUpperCase() === 'EUR') {
        var fixed = ((Number(cents) || 0) / 100).toFixed(2).split('.');
        return fixed[0].replace(/\B(?=(\d{3})+(?!\d))/g, '\u202f') + ',' + fixed[1] + '\u00a0€';
      }
      return formatMoney(cents, this.dataset.moneyFormat);
    }

    /* Même répartition que la remise de l'app « Set d'automne » : la ligne offerte à 0, l'écart entre les pièces
       payantes et le prix du set (data-set-cents) partagé à parts égales, le centime restant à la première. */
    refreshPrices() {
      var self = this;
      var setCents = parseInt(this.dataset.setCents, 10) || 0;
      var paid = this.lines.filter(function (line) { return !line.free && !line.missing; });
      var shares = paid.map(function () { return 0; });
      var priced = paid.every(function (line) { return line.variant && Number(line.variant.price) > 0; });
      if (setCents > 0 && paid.length > 0 && priced) {
        var sum = paid.reduce(function (acc, line) { return acc + Number(line.variant.price); }, 0);
        var reduction = sum - setCents;
        if (reduction > 0) {
          var base = Math.floor(reduction / paid.length);
          var remainder = reduction - base * paid.length;
          shares = paid.map(function (line) {
            var share = base + (remainder > 0 ? 1 : 0);
            if (remainder > 0) remainder -= 1;
            return Math.min(share, Number(line.variant.price));
          });
        }
      }
      this.lines.forEach(function (line) {
        if (line.missing || !line.variant) return;
        var price = Number(line.variant.price) || 0;
        if (line.free) {
          if (line.compareEl) line.compareEl.textContent = self.formatPrice(price);
          return;
        }
        var index = paid.indexOf(line);
        var share = index > -1 ? shares[index] : 0;
        if (line.compareEl) {
          line.compareEl.textContent = self.formatPrice(price);
          line.compareEl.hidden = share <= 0;
        }
        if (line.priceEl) line.priceEl.textContent = self.formatPrice(price - share);
      });
    }

    setButtonState(complete) {
      if (!this.button) return;
      this.button.disabled = !complete;
      if (this.buttonLabel) {
        this.buttonLabel.textContent = complete ? this.dataset.addText || '' : this.dataset.soldOutText || this.dataset.addText || '';
      }
    }

    refreshAll() {
      var self = this;
      /* D'abord les lignes autonomes, puis celles qui suivent un coloris. */
      this.lines.filter(function (line) { return !self.isFollower(line); }).forEach(this.refreshLine, this);
      this.lines.filter(function (line) { return self.isFollower(line); }).forEach(this.refreshLine, this);
      this.refreshSummary();
    }

    isFollower(line) {
      for (var i = 0; i < line.options.length; i++) {
        if (line.options[i].follow) return true;
      }
      return false;
    }

    onChange(event) {
      var control = event.target;
      if (!control || !control.hasAttribute || !control.hasAttribute('data-set-input')) return;
      var lineEl = control.closest('[data-set-line]');
      var line = this.lineByElement(lineEl);
      if (!line) return;
      var wrapper = control.closest('[data-set-option]');
      var kind = wrapper ? wrapper.getAttribute('data-set-option') : '';

      this.refreshLine(line);
      this.followersOf(line.role).forEach(this.refreshLine, this);
      this.refreshSummary();
      this.hideError();

      if (kind === 'colour' && line.gallerySource) {
        var option = null;
        for (var i = 0; i < line.options.length; i++) {
          if (line.options[i].el === wrapper) option = line.options[i];
        }
        this.switchGallery(control.value, option ? option.colours : []);
      }
    }

    /* ------------------------------------------------------------ galerie */

    altMatches(alt, handle) {
      if (!handle) return false;
      return new RegExp('(^|[^a-z0-9])' + escapeRegExp(handle) + '([^a-z0-9]|$)').test(alt);
    }

    /* Image d'une autre variante de la ligne au même coloris (disponible de préférence). */
    colourVariantImage(line, option, value) {
      if (!option || option.index < 0 || !value) return '';
      var key = 'option' + (option.index + 1);
      var fallback = '';
      for (var i = 0; i < line.variants.length; i++) {
        var variant = line.variants[i];
        if (variant[key] !== value || !variant.featured_image) continue;
        if (variant.available) return variant.featured_image;
        if (!fallback) fallback = variant.featured_image;
      }
      return fallback;
    }

    /* Vignette tirée des médias du produit set pour ce coloris (alt ou nom de fichier). */
    setMediaImage(colour, colours, role) {
      var hit = this.findMedia(colour, colours, role);
      return hit && hit.src ? hit.src : '';
    }

    /* Médias du produit set dont l'alt ou le nom de fichier contient le coloris (mot entier) ; un coloris plus
       long qui contient celui-ci (« Bleu Marine » pour « Bleu ») et qui figure aussi dans le texte l'emporte.
       Parmi les candidats, celui qui nomme aussi la pièce (short, brassière, legging) passe devant. */
    findMedia(colour, colours, role) {
      if (!colour || !this.media.length) return null;
      var needle = handleize(colour);
      if (!needle) return null;
      var longer = (colours || []).map(function (c) { return c.handle; }).filter(function (h) {
        return h && h !== needle && h.length > needle.length && h.indexOf(needle) > -1;
      });
      var roleWords = { short: ['short'], brassiere: ['brassiere', 'bra'], legging: ['legging'] }[role] || [];
      var first = null;
      for (var i = 0; i < this.media.length; i++) {
        var text = handleize((this.media[i].alt || '') + ' ' + (this.media[i].file || ''));
        if (!this.altMatches(text, needle)) continue;
        var shadowed = false;
        for (var j = 0; j < longer.length; j++) {
          if (this.altMatches(text, longer[j])) {
            shadowed = true;
            break;
          }
        }
        if (shadowed) continue;
        for (var k = 0; k < roleWords.length; k++) {
          if (this.altMatches(text, roleWords[k])) return this.media[i];
        }
        if (!first) first = this.media[i];
      }
      return first;
    }

    switchGallery(colour, colours) {
      var hit = this.findMedia(colour, colours, 'short');
      /* Aucun média pour ce coloris : on revient sur la première image plutôt que de laisser celle d'un autre coloris. */
      if (!hit && this.media.length && colour) hit = this.media[0];
      if (!hit || !hit.id) return;

      var gallery = document.getElementById('MediaGallery-' + (this.dataset.sectionId || ''));
      if (!gallery) return;
      var slide = gallery.querySelector('[data-media-id="' + hit.id + '"]');
      if (slide && slide.classList.contains('is-active')) return;

      if (typeof gallery.setActiveMedia === 'function') {
        try {
          gallery.setActiveMedia(hit.id, false);
          return;
        } catch (e) {
          /* repli : clic sur la vignette */
        }
      }
      var thumb = gallery.querySelector('[data-target="' + hit.id + '"]');
      if (thumb) {
        var button = thumb.querySelector('button') || thumb;
        if (typeof button.click === 'function') button.click();
      }
    }

    /* ------------------------------------------------------------ erreurs */

    showError(text, withFallback) {
      if (this.errorWrapper && this.errorText) {
        this.errorText.textContent = text || '';
        this.errorWrapper.hidden = !text;
      }
      if (withFallback && this.fallback) this.fallback.hidden = false;
    }

    hideError() {
      if (this.errorWrapper) this.errorWrapper.hidden = true;
      if (this.errorText) this.errorText.textContent = '';
      if (this.fallback && this.dataset.ready === 'true') this.fallback.hidden = true;
    }

    setBusy(busy) {
      this.classList.toggle('is-adding', busy);
      if (!this.button) return;
      this.button.setAttribute('aria-disabled', busy ? 'true' : 'false');
      this.button.classList.toggle('loading', busy);
      var spinner = this.button.querySelector('.loading__spinner');
      if (spinner) spinner.classList.toggle('hidden', !busy);
    }

    /* ------------------------------------------------------------ panier */

    buildItems() {
      var groupId = 'set-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
      var items = [];
      for (var i = 0; i < this.lines.length; i++) {
        var line = this.lines[i];
        if (line.missing || !line.variant || !line.variant.available) return null;
        var properties = {};
        properties[this.groupKey] = groupId;
        properties[this.roleKey] = line.role;
        items.push({ id: line.variant.id, quantity: 1, properties: properties });
      }
      return items.length === this.lines.length && items.length > 0 ? items : null;
    }

    cartElement() {
      return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    }

    cartAddUrl() {
      var url = this.dataset.cartAddUrl || '/cart/add';
      if (!/\.js(?=[?#]|$)/.test(url)) url = url.replace(/\/cart\/add(?=[?#]|$)/, '/cart/add.js');
      return url;
    }

    cartUrl() {
      return this.dataset.cartUrl || (window.routes && window.routes.cart_url) || '/cart';
    }

    onAddClick(event) {
      event.preventDefault();
      if (this.adding || !this.button || this.button.disabled) return;
      if (this.button.getAttribute('aria-disabled') === 'true') return;

      var items = this.buildItems();
      if (!items) {
        this.refreshAll();
        this.showError(this.dataset.unavailableText || '', false);
        return;
      }

      var self = this;
      var operations = window.LuveonCartOperations;
      var locked = false;
      if (operations && typeof operations.begin === 'function') {
        if (!operations.begin()) return;
        locked = true;
      }

      this.adding = true;
      this.hideError();
      this.setBusy(true);

      var cart = this.cartElement();
      var body = { items: items };
      if (cart && typeof cart.getSectionsToRender === 'function') {
        try {
          body.sections = cart.getSectionsToRender().map(function (section) { return section.id; }).join(',');
          body.sections_url = window.location.pathname;
        } catch (e) {
          delete body.sections;
          delete body.sections_url;
        }
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
      }

      var request = operations && typeof operations.request === 'function'
        ? operations.request.bind(operations)
        : window.fetch.bind(window);

      request(this.cartAddUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(body)
      })
        .then(function (response) {
          return response.json().then(function (json) {
            return { ok: response.ok, json: json || {} };
          });
        })
        .then(function (result) {
          var json = result.json;
          if (!result.ok || json.status) {
            var description = json.description || json.message || self.dataset.errorText || '';
            if (typeof description !== 'string') description = self.dataset.errorText || '';
            self.publishEvent('cartError', { errors: json.errors || json.description, message: json.message });
            self.showError(description, true);
            return;
          }
          self.afterAdd(json, cart);
        })
        .catch(function () {
          self.showError(self.dataset.errorText || '', true);
        })
        .then(function () {
          self.adding = false;
          self.setBusy(false);
          if (locked && typeof operations.end === 'function') operations.end();
        });
    }

    publishEvent(name, detail) {
      if (typeof window.publish !== 'function' || !window.PUB_SUB_EVENTS || !window.PUB_SUB_EVENTS[name]) return;
      try {
        var payload = { source: 'lma-set-bundle' };
        for (var key in detail) {
          if (Object.prototype.hasOwnProperty.call(detail, key)) payload[key] = detail[key];
        }
        var result = window.publish(window.PUB_SUB_EVENTS[name], payload);
        if (result && typeof result.catch === 'function') result.catch(function () {});
      } catch (e) {
        /* sans abonnés */
      }
    }

    afterAdd(json, cart) {
      this.publishEvent('cartUpdate', { cartData: json });
      this.dispatchEvent(new CustomEvent('lma-set-bundle:added', { bubbles: true, detail: json }));

      if (typeof window.monster_refresh === 'function') {
        try {
          window.monster_refresh();
          return;
        } catch (e) {
          /* on continue avec le drawer Dawn */
        }
      }

      if (cart && json.sections && typeof cart.renderContents === 'function') {
        try {
          if (cart.tagName.toLowerCase() === 'cart-notification' && json.items && json.items.length) {
            json.key = json.items[0].key;
          }
          cart.renderContents(json);
          return;
        } catch (e) {
          /* repli : rechargement du drawer */
        }
      }

      var self = this;
      if (cart && typeof cart.refreshContents === 'function') {
        try {
          var refreshed = cart.refreshContents();
          if (refreshed && typeof refreshed.catch === 'function') {
            refreshed.catch(function () { window.location.href = self.cartUrl(); });
          }
          return;
        } catch (e) {
          /* repli : page panier */
        }
      }

      window.location.href = this.cartUrl();
    }
  }

  window.customElements.define('lma-set-bundle', LmaSetBundle);
})();
